import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Send, Loader2, ExternalLink } from 'lucide-react';
import JessicaAvatar from './JessicaAvatar';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';

const apiPath = (path) => `${API_URL}${path}`;

function renderMarkdownish(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part.split('\n').map((line, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}

export default function JessicaChatbot({ variant = 'global' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', inquiry: '' });
  const [formBusy, setFormBusy] = useState(false);
  const [formDone, setFormDone] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);
  const booted = useRef(false);

  const [backendReady, setBackendReady] = useState(null);

  const appendMessage = useCallback((role, content, extra = {}) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, role, content, ...extra }]);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(apiPath('/api/jessica/config'));
      if (!res.ok) {
        setBackendReady(false);
        if (!booted.current) {
          booted.current = true;
          appendMessage(
            'assistant',
            `Hmm… I can't reach my brain right now. `
            + 'Make sure the backend on port 8002 is running and was restarted after the Jessica update, then refresh this page.',
          );
        }
        return;
      }
      setBackendReady(true);
      const data = await res.json();
      setSuggestions(data.suggestions || []);
      if (!booted.current) {
        booted.current = true;
        appendMessage('assistant', data.greeting || "Hey, I'm Jessica — your Snagcart guide.");
      }
    } catch (_) {
      setBackendReady(false);
      if (!booted.current) {
        booted.current = true;
        appendMessage(
          'assistant',
          "Can't connect to the server. Is the backend running on port 8002? Refresh after starting it.",
        );
      }
    }
  }, [appendMessage]);

  useEffect(() => {
    if (open && !booted.current) loadConfig();
  }, [open, loadConfig]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, showForm, formDone]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    appendMessage('user', msg);
    setLoading(true);
    setShowForm(false);

    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch(apiPath('/api/jessica/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history }),
      });
      let data;
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const detail = errBody.detail || errBody.message;
        if (res.status === 404) {
          data = {
            reply: 'My chat API isn\'t loaded yet. Restart the backend (port 8002) and refresh — the /api/jessica routes need a fresh server start.',
          };
        } else {
          data = {
            reply: detail
              ? `Something broke on my end. ${typeof detail === 'string' ? detail : 'Try again.'}`
              : "I'm having a moment — try again in a sec.",
          };
        }
      } else {
        data = await res.json();
      }
      appendMessage('assistant', data.reply, { link: data.link, linkLabel: data.link_label });
      if (data.show_form) setShowForm(true);
      if (data.suggestions?.length) setSuggestions(data.suggestions);
    } catch (_) {
      appendMessage('assistant', "Couldn't reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.email || !form.phone || !form.inquiry) return;
    setFormBusy(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(apiPath('/api/jessica/inquiry'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...form,
          source: variant === 'landing' ? 'jessica_landing' : 'jessica_dashboard',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setFormDone(true);
      setShowForm(false);
      appendMessage('assistant', data.message || 'Sent! Our team will reply soon.');
      setForm({ name: '', email: '', phone: '', inquiry: '' });
    } catch (err) {
      appendMessage('assistant', `Couldn't send that. ${err.message || 'Try again.'}`);
    } finally {
      setFormBusy(false);
    }
  };

  const openContactForm = () => {
    setShowForm(true);
    appendMessage('assistant', "No problem — drop your email, phone, and question below. I'll forward it to the team.");
  };

  const followLink = (path) => {
    if (!path) return;
    setOpen(false);
    if (path.startsWith('http')) window.open(path, '_blank');
    else navigate(path);
  };

  const hidePaths = ['/login', '/auth', '/reset-password'];
  const isWhiteLabelAuth = /^\/w\/[^/]+\/auth$/.test(location.pathname);
  if (hidePaths.includes(location.pathname) || isWhiteLabelAuth) return null;

  const token = localStorage.getItem('token');
  const onLanding = location.pathname === '/';
  if (!token && !onLanding) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 left-4 sm:left-6 z-[90] flex items-center gap-2.5 rounded-full border border-amber-400/30 bg-[#0d0d14]/95 pl-1 pr-4 py-1 shadow-xl shadow-amber-500/20 hover:scale-105 hover:border-amber-400/50 transition-all"
        aria-label="Chat with Jessica"
        data-testid="jessica-chat-fab"
      >
        <JessicaAvatar size="lg" ring className="shadow-md shadow-amber-500/30" />
        <span className="text-sm font-semibold text-white">Jessica</span>
      </button>

      {open && (
        <div
          className="fixed bottom-24 left-4 sm:left-6 z-[91] flex w-[min(100vw-2rem,380px)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d14] shadow-2xl shadow-black/50 max-h-[min(85vh,620px)]"
          data-testid="jessica-chat-panel"
        >
          {/* Header */}
          <div className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-gradient-to-r from-amber-500/15 to-orange-500/10 px-4 py-3">
            <JessicaAvatar size="md" ring />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm">Jessica</p>
              <p className="text-[11px] text-zinc-500 truncate">
                {showForm && !formDone
                  ? 'Contact form — we’ll email the team'
                  : backendReady === false
                    ? 'Offline — restart backend :8002'
                    : 'Your Snagcart guide · here to help'}
              </p>
            </div>
            {showForm && !formDone ? (
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded border border-white/10"
              >
                Back
              </button>
            ) : null}
            <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white p-1">
              <X size={18} />
            </button>
          </div>

          {/* Contact form — dedicated view so submit is always visible */}
          {showForm && !formDone ? (
            <>
              <form
                id="jessica-inquiry-form"
                onSubmit={submitInquiry}
                className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3"
              >
                <p className="text-xs text-zinc-400">Email, phone, and your question — we’ll get back to you.</p>
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600"
                />
                <input
                  type="email"
                  required
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600"
                />
                <input
                  type="tel"
                  required
                  placeholder="Phone *"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600"
                />
                <textarea
                  required
                  placeholder="Your inquiry *"
                  rows={4}
                  value={form.inquiry}
                  onChange={(e) => setForm({ ...form, inquiry: e.target.value })}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 resize-none"
                />
              </form>
              <div className="shrink-0 border-t border-white/10 bg-[#0d0d14] p-3 space-y-2">
                <button
                  type="submit"
                  form="jessica-inquiry-form"
                  disabled={formBusy || !form.email || !form.phone || !form.inquiry.trim()}
                  className="w-full rounded-xl bg-amber-500 py-3 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20"
                  data-testid="jessica-inquiry-submit"
                >
                  {formBusy ? 'Sending…' : 'Submit inquiry'}
                </button>
                <p className="text-center text-[10px] text-zinc-600">Sent to our support team</p>
              </div>
            </>
          ) : (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {m.role === 'assistant' && <JessicaAvatar size="xs" className="mt-1" />}
                    <div
                      className={`max-w-[82%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-violet-600 text-white rounded-br-md'
                          : 'bg-white/[0.06] text-zinc-300 rounded-bl-md border border-white/5'
                      }`}
                    >
                      {renderMarkdownish(m.content)}
                      {m.link && (
                        <button
                          type="button"
                          onClick={() => followLink(m.link)}
                          className="mt-2 flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200"
                        >
                          <ExternalLink size={11} />
                          {m.linkLabel || 'Open'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex items-center gap-2 text-xs text-zinc-500 pl-7">
                    <Loader2 size={14} className="animate-spin" />
                    Jessica is typing…
                  </div>
                )}
              </div>

              {suggestions.length > 0 && (
                <div className="shrink-0 px-3 pb-2 flex flex-wrap gap-1.5">
                  {suggestions.slice(0, 3).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => sendMessage(s)}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] text-zinc-400 hover:text-white hover:border-amber-500/30"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <div className="shrink-0 border-t border-white/10 p-3 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Ask Jessica anything…"
                  className="flex-1 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/40"
                />
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={loading || !input.trim()}
                  className="rounded-xl bg-violet-600 p-2 text-white hover:bg-violet-500 disabled:opacity-50"
                >
                  <Send size={16} />
                </button>
              </div>

              <div className="shrink-0 px-3 pb-3 flex justify-center">
                <button
                  type="button"
                  onClick={openContactForm}
                  className="text-xs text-amber-400/90 hover:text-amber-300 font-medium underline"
                >
                  Contact our team →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
